export type TChatHistoryMessage = {
  role: "user" | "assistant";
  content: string;
  id: string;
};

export type TChatHistorySlice<TMessage extends TChatHistoryMessage, TLoading> = {
  chatHistory: Record<string, TMessage[]>;
  loadingStates: Record<string, TLoading>;
  getChatHistory: (key: string) => TMessage[];
  addMessage: (key: string, message: TMessage) => void;
  clearChatHistory: (key: string) => void;
  removeLastMessage: (key: string) => void;
  getLoadingState: (key: string) => TLoading;
  setLoadingState: (key: string, updates: Partial<TLoading>) => void;
  resetLoadingState: (key: string) => void;
  getMessagesWithPending: (key: string) => TMessage[];
};

type TSliceStateShape<TMessage extends TChatHistoryMessage, TLoading> = {
  chatHistory: Record<string, TMessage[]>;
  loadingStates: Record<string, TLoading>;
};

export function createChatHistorySlice<
  TMessage extends TChatHistoryMessage,
  TLoading extends { pendingBotMessage: TMessage | null }
>(
  set: (updater: (state: TSliceStateShape<TMessage, TLoading>) => Partial<TSliceStateShape<TMessage, TLoading>>) => void,
  get: () => TSliceStateShape<TMessage, TLoading>,
  createDefaultLoadingState: () => TLoading
): TChatHistorySlice<TMessage, TLoading> {
  return {
    chatHistory: {},
    loadingStates: {},

    getChatHistory: (key) => get().chatHistory[key] || [],

    addMessage: (key, message) => {
      set((state) => {
        const messages = state.chatHistory[key] || [];
        if (messages.some((m) => m.id === message.id)) return state;
        return { chatHistory: { ...state.chatHistory, [key]: [...messages, message] } };
      });
    },

    clearChatHistory: (key) => {
      set((state) => ({ chatHistory: { ...state.chatHistory, [key]: [] } }));
    },

    removeLastMessage: (key) => {
      set((state) => {
        const messages = state.chatHistory[key] || [];
        if (messages.length === 0) return state;
        return { chatHistory: { ...state.chatHistory, [key]: messages.slice(0, -1) } };
      });
    },

    getLoadingState: (key) => get().loadingStates[key] || createDefaultLoadingState(),

    setLoadingState: (key, updates) => {
      set((state) => {
        const current = state.loadingStates[key] || createDefaultLoadingState();
        return { loadingStates: { ...state.loadingStates, [key]: { ...current, ...updates } } };
      });
    },

    resetLoadingState: (key) => {
      set((state) => ({ loadingStates: { ...state.loadingStates, [key]: createDefaultLoadingState() } }));
    },

    getMessagesWithPending: (key) => {
      const messages = get().chatHistory[key] || [];
      const pending = (get().loadingStates[key] || createDefaultLoadingState()).pendingBotMessage;
      return pending ? [...messages, pending] : messages;
    }
  };
}
